import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import {
    Users,
    DollarSign,
    TrendingUp,
    Package,
    Calendar,
    Award,
    MousePointer
} from 'lucide-react';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import AnalyticsCharts from '../../components/AnalyticsCharts';

interface AdminStats {
    totalAffiliates: number;
    activeAffiliates: number;
    totalProducts: number;
    totalEarnings: number;
    totalClicks: number;
    totalConversions: number;
    conversionRate: string;
}

interface TopAffiliate {
    id: string;
    email: string;
    companyName: string;
    earnings: number;
    conversions: number;
}

interface ProductPerformance {
    productId: string;
    productName: string;
    conversions: number;
    revenue: number;
}



export default function AdminDashboard() {
    const [stats, setStats] = useState<AdminStats>({
        totalAffiliates: 0,
        activeAffiliates: 0,
        totalProducts: 0,
        totalEarnings: 0,
        totalClicks: 0,
        totalConversions: 0,
        conversionRate: '0%',
    });
    const [topAffiliates, setTopAffiliates] = useState<TopAffiliate[]>([]);
    const [productPerformance, setProductPerformance] = useState<ProductPerformance[]>([]);
    const [monthlyData, setMonthlyData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState('0');

    useEffect(() => {
        fetchStats();
    }, [dateRange]);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const days = parseInt(dateRange);
            const startDate = new Date();
            if (days === 0) {
                startDate.setHours(0, 0, 0, 0);
            } else {
                startDate.setDate(startDate.getDate() - days);
            }

            // Fetch affiliates
            const affiliatesSnapshot = await getDocs(
                query(collection(db, 'users'), where('role', '==', 'affiliate'))
            );
            const affiliates = affiliatesSnapshot.docs.map(doc => ({
                id: doc.id,
                email: doc.data().email || '',
                company_details: doc.data().company_details || { name: '' },
                ...doc.data()
            }));

            // Fetch products
            const productsSnapshot = await getDocs(collection(db, 'products'));
            const products = productsSnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name || 'Unknown',
                ...doc.data()
            }));

            // Fetch clicks
            const clicksQuery = query(
                collection(db, 'clicks'),
                where('timestamp', '>=', startDate),
                orderBy('timestamp', 'desc')
            );
            const clicksSnapshot = await getDocs(clicksQuery);
            const clicks = clicksSnapshot.docs.map(doc => doc.data());

            // Fetch conversions
            const conversionsQuery = query(
                collection(db, 'conversions'),
                where('timestamp', '>=', startDate),
                orderBy('timestamp', 'desc')
            );
            const conversionsSnapshot = await getDocs(conversionsQuery);
            const conversions = conversionsSnapshot.docs.map(doc => doc.data());

            // Calculate stats
            const totalEarnings = conversions.reduce((sum, c) => sum + (c.commission_generated || 0), 0);
            const conversionRate = clicks.length > 0
                ? ((conversions.length / clicks.length) * 100).toFixed(2) + '%'
                : '0%';

            // Active affiliates (those with activity in the period)
            const activeAffiliateIds = new Set(
                [...clicks, ...conversions].map(item => item.affiliate_id)
            );

            setStats({
                totalAffiliates: affiliates.length,
                activeAffiliates: activeAffiliateIds.size,
                totalProducts: products.length,
                totalEarnings,
                totalClicks: clicks.length,
                totalConversions: conversions.length,
                conversionRate,
            });

            // Calculate top affiliates
            const affiliateEarnings = new Map<string, { earnings: number; conversions: number }>();
            conversions.forEach(c => {
                const existing = affiliateEarnings.get(c.affiliate_id) || { earnings: 0, conversions: 0 };
                affiliateEarnings.set(c.affiliate_id, {
                    earnings: existing.earnings + (c.commission_generated || 0),
                    conversions: existing.conversions + 1,
                });
            });

            const topAffiliatesData = Array.from(affiliateEarnings.entries())
                .map(([id, data]) => {
                    const affiliate = affiliates.find(a => a.id === id);
                    return {
                        id,
                        email: affiliate?.email || 'Unknown',
                        companyName: affiliate?.company_details?.name || 'Unknown',
                        earnings: data.earnings,
                        conversions: data.conversions,
                    };
                })
                .sort((a, b) => b.earnings - a.earnings)
                .slice(0, 5);

            setTopAffiliates(topAffiliatesData);

            // Calculate product performance
            const productStats = new Map<string, { conversions: number; revenue: number }>();
            conversions.forEach(c => {
                const existing = productStats.get(c.product_id) || { conversions: 0, revenue: 0 };
                productStats.set(c.product_id, {
                    conversions: existing.conversions + 1,
                    revenue: existing.revenue + (c.amount || 0),
                });
            });

            const productPerformanceData = Array.from(productStats.entries())
                .map(([productId, data]) => {
                    const product = products.find(p => p.id === productId);
                    return {
                        productId,
                        productName: product?.name || 'Unknown Product',
                        conversions: data.conversions,
                        revenue: data.revenue,
                    };
                })
                .sort((a, b) => b.revenue - a.revenue)
                .slice(0, 5);

            setProductPerformance(productPerformanceData);

            // Prepare monthly data for chart
            const monthlyMap = new Map<string, { clicks: number; conversions: number; revenue: number }>();

            const loopDays = days === 0 ? 1 : days;
            for (let i = 0; i < loopDays; i++) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateStr = date.toISOString().split('T')[0];
                monthlyMap.set(dateStr, { clicks: 0, conversions: 0, revenue: 0 });
            }

            clicks.forEach(click => {
                const dateStr = click.timestamp.toDate().toISOString().split('T')[0];
                const existing = monthlyMap.get(dateStr);
                if (existing) {
                    monthlyMap.set(dateStr, { ...existing, clicks: existing.clicks + 1 });
                }
            });

            conversions.forEach(conv => {
                const dateStr = conv.timestamp.toDate().toISOString().split('T')[0];
                const existing = monthlyMap.get(dateStr);
                if (existing) {
                    monthlyMap.set(dateStr, {
                        ...existing,
                        conversions: existing.conversions + 1,
                        revenue: existing.revenue + (conv.amount || 0),
                    });
                }
            });

            const monthlyDataArray = Array.from(monthlyMap.entries())
                .map(([date, data]) => ({
                    date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                    ...data,
                    conversionRate: data.clicks > 0 ? (data.conversions / data.clicks) * 100 : 0
                }))
                .reverse();

            setMonthlyData(monthlyDataArray);

        } catch (error) {
            console.error('Error fetching admin stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const statCards = [
        {
            title: 'Total Clicks',
            value: stats.totalClicks.toLocaleString(),
            icon: MousePointer,
            color: 'from-blue-500 to-cyan-500',
        },
        {
            title: 'Conversions',
            value: stats.totalConversions.toLocaleString(),
            subtitle: stats.conversionRate + ' rate',
            icon: TrendingUp,
            color: 'from-amber-500 to-orange-500',
        },
        {
            title: 'Total Revenue',
            value: '€' + stats.totalEarnings.toFixed(2),
            icon: DollarSign,
            color: 'from-emerald-500 to-green-500',
        },
        {
            title: 'Total Affiliates',
            value: stats.totalAffiliates.toLocaleString(),
            subtitle: `${stats.activeAffiliates} active`,
            icon: Users,
            color: 'from-purple-500 to-pink-500',
        },
        {
            title: 'Total Products',
            value: stats.totalProducts.toLocaleString(),
            icon: Package,
            color: 'from-rose-500 to-red-500',
        },
    ];

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
                    <p className="text-gray-400">Platform overview and analytics</p>
                </div>

                <div className="flex items-center gap-2">
                    <Calendar size={20} className="text-gray-400" />
                    <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="input-field py-2 px-3 w-auto"
                    >
                        <option value="0">Today</option>
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                    </select>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {statCards.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <div
                            key={stat.title}
                            className="stat-card animate-slide-up"
                            style={{ animationDelay: `${index * 100}ms` }}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className={`w-12 h-12 rounded-xl bg-linear-to-br ${stat.color} flex items-center justify-center`}>
                                    <Icon className="text-white" size={24} />
                                </div>
                            </div>
                            <h3 className="text-gray-400 text-sm mb-1">{stat.title}</h3>
                            <p className="text-2xl font-bold text-white">{loading ? '...' : stat.value}</p>
                            {stat.subtitle && (
                                <p className="text-xs text-gray-500 mt-1">{stat.subtitle}</p>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue & Conversions Chart */}
                <div className="card">
                    <h2 className="text-lg font-semibold text-white mb-6">Revenue & Activity</h2>
                    <div className="h-80">
                        {loading ? (
                            <div className="h-full flex items-center justify-center">
                                <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={monthlyData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        stroke="#6B7280"
                                        fontSize={12}
                                        tickLine={false}
                                        axisLine={false}
                                        dy={10}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        stroke="#6B7280"
                                        fontSize={12}
                                        tickLine={false}
                                        axisLine={false}
                                        tickFormatter={(value) => `€${value}`}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        stroke="#6B7280"
                                        fontSize={12}
                                        tickLine={false}
                                        axisLine={false}
                                        tickFormatter={(value) => `${value}%`}
                                        domain={[0, 'auto']}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'rgba(17, 24, 39, 0.95)',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: '8px',
                                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                                        }}
                                        itemStyle={{ fontSize: '12px' }}
                                    />
                                    <Legend
                                        verticalAlign="top"
                                        align="right"
                                        iconType="circle"
                                        wrapperStyle={{ paddingBottom: '20px', fontSize: '12px' }}
                                    />
                                    <Line
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="revenue"
                                        stroke="#10B981"
                                        strokeWidth={3}
                                        dot={false}
                                        activeDot={{ r: 6, strokeWidth: 0 }}
                                        name="Revenue (€)"
                                    />
                                    <Line
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="conversions"
                                        stroke="#6366F1"
                                        strokeWidth={3}
                                        dot={false}
                                        activeDot={{ r: 6, strokeWidth: 0 }}
                                        name="Conversions"
                                    />
                                    <Line
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="conversionRate"
                                        stroke="#F59E0B"
                                        strokeWidth={3}
                                        strokeDasharray="5 5"
                                        dot={false}
                                        activeDot={{ r: 6, strokeWidth: 0 }}
                                        name="Conv. Rate (%)"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Product Performance */}
                <div className="card">
                    <h2 className="text-lg font-semibold text-white mb-6">Top Products by Revenue</h2>
                    <div className="h-80">
                        {loading ? (
                            <div className="h-full flex items-center justify-center">
                                <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={productPerformance}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="revenue"
                                        nameKey="productName"
                                    >
                                        {productPerformance.map((_, index) => (
                                            <Cell key={`cell-${index}`} fill={['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][index % 5]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'rgba(17, 24, 39, 0.95)',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: '8px',
                                        }}
                                        formatter={(value: any) => `€${Number(value).toFixed(2)}`}
                                    />
                                    <Legend
                                        verticalAlign="bottom"
                                        align="center"
                                        iconType="circle"
                                        wrapperStyle={{ paddingTop: '20px', fontSize: '11px' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            {/* Detailed Analytics */}
            <AnalyticsCharts periodDays={parseInt(dateRange)} />

            {/* Top Affiliates */}
            <div className="card">
                <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                    <Award size={20} className="text-amber-400" />
                    Top Performing Affiliates
                </h2>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-white/10">
                                <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">#</th>
                                <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Company</th>
                                <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Email</th>
                                <th className="text-right py-3 px-4 text-gray-400 font-medium text-sm">Conversions</th>
                                <th className="text-right py-3 px-4 text-gray-400 font-medium text-sm">Earnings</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="text-center py-8">
                                        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : topAffiliates.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center py-8 text-gray-500">
                                        No data available
                                    </td>
                                </tr>
                            ) : (
                                topAffiliates.map((affiliate, index) => (
                                    <tr key={affiliate.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                        <td className="py-4 px-4">
                                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${index === 0 ? 'bg-amber-500/20 text-amber-400' :
                                                index === 1 ? 'bg-gray-500/20 text-gray-400' :
                                                    index === 2 ? 'bg-orange-500/20 text-orange-400' :
                                                        'bg-white/5 text-gray-500'
                                                } text-xs font-bold`}>
                                                {index + 1}
                                            </span>
                                        </td>
                                        <td className="py-4 px-4 text-white font-medium">{affiliate.companyName}</td>
                                        <td className="py-4 px-4 text-gray-400 text-sm">{affiliate.email}</td>
                                        <td className="py-4 px-4 text-right text-white">{affiliate.conversions}</td>
                                        <td className="py-4 px-4 text-right">
                                            <span className="text-emerald-400 font-semibold">
                                                €{affiliate.earnings.toFixed(2)}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
